import { FC } from "react";
import ResponsiveLayout from "../ResponsiveLayout/ResponsiveLayout";
import styled from "styled-components";
import {
  BackgroundColorProps,
  BorderColorProps,
  ColorProps,
} from "styled-system";

import Box from "../Box/Box";
import ItemBlock from "./ItemBlock";
import { IconWeight } from "../Icon/Icon";

export type ItemProps = {
  id: string;
  title: string;
  description: string;
  label: "number" | "icon";
  number?: string;
  iconName?: string;
  iconWeight?: IconWeight;
};

export interface HorizontalTimelineProps {
  /**
   <h4>ItemProps:</h4><br>
    - <b>id:</b> string - unique identifier<br>
    - <b>title:</b>  string<br>
    - <b>description:</b>  string<br>
    - <b>label:</b>  "number" | "icon"<br>
    - <b>number?:</b>  string - optional but must be set if label = number<br>
    - <b>iconName?:</b>  string - optional but must be set if label = icon<br>
    - <b>iconWeight?:</b>  "regular" | "solid" | "light" - optional but must be set if label = icon<br>
    <br>
   */
  items: ItemProps[];
  /**
   * The panel's background color. It accepts either a hex color code or a pattern library color alias.
   */
  backgroundColor?: BackgroundColorProps["backgroundColor"];
  /**
   The color of the label's number
   */
  labelsColor?: ColorProps["color"];
  /**
   The background color of the label's circle
   */
  labelsBackgroundColor?: BackgroundColorProps["backgroundColor"];
  /**
   The color of the label's circle
   */
  labelsBorderColor?: BorderColorProps["borderColor"];
  /**
   The color of the connector line
   */
  connectorColor?: BorderColorProps["borderColor"];
  /**
   The color of the title
   */
  titleColor?: ColorProps["color"];
  /**
   The color of the description
   */
  descriptionColor?: ColorProps["color"];
}

const TimelineContainer = styled(Box)<{ minContainerWidth: number }>`
  ${(props) => `@media screen and (min-width: ${props.minContainerWidth}px) {
    flex-direction: row;
    width: 100%;
  }`}
`;

const HorizontalTimeline: FC<HorizontalTimelineProps> = ({
  items,
  labelsColor = "footer-blue",
  labelsBackgroundColor = "transparent",
  labelsBorderColor = "lightest-grey",
  connectorColor = "lightest-grey",
  titleColor = "grey-blue",
  descriptionColor = "lightest-grey",
  backgroundColor = "transparent",
}) => {
  const minContainerWidth = items.length * 250 + 120;

  return (
    <ResponsiveLayout
      display="flex"
      justifyContent="center"
      backgroundColor={backgroundColor}
      aria-label="horizontal timeline"
    >
      <TimelineContainer
        width="fit-content"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="stretch"
        minContainerWidth={minContainerWidth}
        as="ul"
      >
        {items.slice(0, 5).map((item, index) => (
          <ItemBlock
            key={item.id}
            minContainerWidth={minContainerWidth}
            index={index}
            item={item}
            numberOfItems={items.length - 1}
            labelsColor={labelsColor}
            labelsBackgroundColor={labelsBackgroundColor}
            labelsBorderColor={labelsBorderColor}
            connectorColor={connectorColor}
            titleColor={titleColor}
            descriptionColor={descriptionColor}
          />
        ))}
      </TimelineContainer>
    </ResponsiveLayout>
  );
};

export default HorizontalTimeline;
